// Each period's one-bar drum break and its dawn bar, generated deterministically from the period
// seed and kit (SPEC §1). The per-slot variation is applied by the engine
// (`engine::song::break_variation`).

import type { NoteEvent } from "./generated/NoteEvent";
import { KITS, type KitId, type KitStyle } from "./kits";
import { Rng } from "./rng";

const S = 240; // a 16th note in ticks
const ROOT = 60;

type Hit = [step: number, pad: number, velocity: number];

function ev(step: number, pad: number, velocity: number, lengthSteps = 1): NoteEvent {
  return {
    tick: step * S,
    pad,
    note: ROOT,
    velocity: Math.max(1, Math.min(127, Math.round(velocity))),
    length_ticks: lengthSteps * S,
  };
}

/** Templates per style: [kick steps, snare steps, hat steps] with velocities. */
const TEMPLATES: Record<
  KitStyle,
  { kick: number[]; snare: number[]; ghosts: number[]; hats: number[]; open: number[] }[]
> = {
  breakbeat: [
    { kick: [0, 10], snare: [4, 12], ghosts: [7, 15], hats: [0, 2, 4, 6, 8, 10, 12, 14], open: [] },
    { kick: [0, 2, 10], snare: [4, 12], ghosts: [9], hats: [0, 2, 4, 6, 8, 10, 12, 14], open: [14] },
    { kick: [0, 6, 10], snare: [4, 12], ghosts: [11, 15], hats: [0, 2, 4, 6, 8, 10, 12], open: [] },
  ],
  dub: [
    { kick: [0, 8], snare: [12], ghosts: [], hats: [2, 6, 10, 14], open: [] },
    { kick: [0, 7, 8], snare: [12], ghosts: [15], hats: [2, 6, 10, 14], open: [6] },
  ],
  halftime: [
    { kick: [0, 3, 10], snare: [8], ghosts: [], hats: [0, 2, 4, 6, 8, 10, 12, 13, 14, 15], open: [] },
    { kick: [0, 6, 11], snare: [8], ghosts: [15], hats: [0, 2, 4, 6, 8, 10, 11, 12, 14], open: [] },
  ],
  four_floor: [
    { kick: [0, 4, 8, 12], snare: [4, 12], ghosts: [], hats: [2, 6, 10, 14], open: [] },
    { kick: [0, 4, 8, 12], snare: [4, 12], ghosts: [15], hats: [1, 3, 5, 7, 9, 11, 13, 15], open: [2, 10] },
  ],
};

function sortEvents(v: NoteEvent[]): NoteEvent[] {
  return v.sort((a, b) => a.tick - b.tick || a.pad - b.pad);
}

/** Slow-stream (72 BPM) breaks: half-time kick/snare with double-time hats — 16ths, triplet and
 *  32nd rolls (in ticks: a 16th is 240, a 32nd 120, a 16th triplet 160) — or a laid-back jam. */
const SLOW_TEMPLATES: { kick: number[]; snare: number[]; ghosts: number[]; rolls: number[][]; open: number[] }[] = [
  // Trap: kick 1 and the "a" of 2, snare on 3, hats in 16ths with a 32nd roll into beat 4.
  { kick: [0, 7, 10], snare: [8], ghosts: [], rolls: [[2880, 120, 4]], open: [] },
  // Trap with a triplet roll on beat 2 and a 32nd run at the end.
  {
    kick: [0, 3, 11],
    snare: [8],
    ghosts: [15],
    rolls: [
      [960, 160, 6],
      [3360, 120, 4],
    ],
    open: [],
  },
  // Slow jam: backbeat on 2 and 4, lazy kick, an open hat on the "and" of 4.
  { kick: [0, 7, 10], snare: [4, 12], ghosts: [6, 15], rolls: [], open: [14] },
  // Dusty half-time with ghost notes.
  { kick: [0, 2, 10], snare: [8], ghosts: [5, 13], rolls: [[3600, 120, 2]], open: [6] },
];

function generateSlowBreak(kit: KitId, seed: number): NoteEvent[] {
  const r = KITS[kit].roles;
  const rng = new Rng(seed ^ 0x72b4_0001);
  const t = rng.pick(SLOW_TEMPLATES);
  const out: NoteEvent[] = [];
  const at = (tick: number, pad: number, velocity: number, len = 120): NoteEvent => ({
    tick,
    pad,
    note: ROOT,
    velocity: Math.max(1, Math.min(127, Math.round(velocity))),
    length_ticks: len,
  });
  for (const s of t.kick) out.push(at(s * S, r.kick, s === 0 ? 120 : rng.int(98, 114), S));
  for (const s of t.snare) out.push(at(s * S, r.snare, rng.int(106, 120), S));
  for (const s of t.ghosts) if (rng.chance(0.75)) out.push(at(s * S, r.snare, rng.int(28, 44)));
  // Rolls replace the hats they cover.
  const rolled = (tick: number) => t.rolls.some(([from, step, n]) => tick >= from && tick < from + step * n);
  for (let s = 0; s < 16; s++) {
    if (t.open.includes(s) || rolled(s * S)) continue;
    if (t.rolls.length === 0 && s % 2 === 1) continue; // the slow jam rides 8ths
    out.push(at(s * S, r.closed_hat, s % 4 === 0 ? rng.int(84, 98) : rng.int(52, 72)));
  }
  for (const [from, step, n] of t.rolls) {
    for (let k = 0; k < n; k++) out.push(at(from + k * step, r.closed_hat, 54 + (k * 34) / Math.max(1, n - 1), step));
  }
  for (const s of t.open) out.push(at(s * S, r.open_hat, rng.int(80, 94), 2 * S));
  return sortEvents(out);
}

/** The period's base break. Deterministic in (kit, seed, bpm). */
export function generateBreak(kit: KitId, seed: number, bpm = 125): NoteEvent[] {
  if (bpm === 72) return generateSlowBreak(kit, seed);
  const info = KITS[kit];
  const r = info.roles;
  const rng = new Rng(seed ^ 0xb4ea_0001);
  const t = rng.pick(TEMPLATES[info.style]);
  const hits: Hit[] = [];
  for (const s of t.kick) hits.push([s, r.kick, s === 0 ? 118 : rng.int(96, 112)]);
  for (const s of t.snare) hits.push([s, r.snare, rng.int(104, 118)]);
  for (const s of t.ghosts) if (rng.chance(0.7)) hits.push([s, r.snare, rng.int(30, 48)]);
  // House-style kits clap on the backbeat with the snare role; the others get an extra clap
  // sometimes.
  for (const s of t.hats) {
    if (t.open.includes(s)) continue;
    const accent = s % 4 === 0;
    hits.push([s, r.closed_hat, accent ? rng.int(84, 100) : rng.int(58, 76)]);
  }
  for (const s of t.open) hits.push([s, r.open_hat, rng.int(80, 96)]);
  // A sprinkle of the kit's extra percussion.
  const extra = info.extras[0];
  if (extra !== undefined && rng.chance(0.6)) {
    for (const s of [3, 7, 11, 15]) if (rng.chance(0.5)) hits.push([s, extra, rng.int(44, 70)]);
  }
  // Dedup (same step + pad): keep the loudest.
  const best = new Map<string, Hit>();
  for (const h of hits) {
    const k = `${h[0]}:${h[1]}`;
    const prev = best.get(k);
    if (!prev || prev[2] < h[2]) best.set(k, h);
  }
  return sortEvents([...best.values()].map(([s, pad, v]) => ev(s, pad, v)));
}

/** The dawn bar into a day (played with that day's sounds): an accent and a climbing fill. */
export function generateDawn(kit: KitId, seed: number): NoteEvent[] {
  const r = KITS[kit].roles;
  const rng = new Rng(seed ^ 0xda_0001);
  const out: NoteEvent[] = [ev(0, r.accent, 118, 4), ev(0, r.kick, 120)];
  if (rng.chance(0.5)) out.push(ev(4, r.snare, 96), ev(10, r.kick, 100));
  else out.push(ev(6, r.kick, 104), ev(8, r.snare, 100));
  const roll = rng.chance(0.5)
    ? [r.fill_low, r.fill_low, r.fill_high, r.fill_high]
    : [r.fill_high, r.fill_low, r.fill_high, r.snare];
  roll.forEach((pad, i) => out.push(ev(12 + i, pad, 82 + i * 11)));
  return sortEvents(out);
}
